import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Icon,
  LoadingIcon,
  Stack,
  Text,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { useNamedLogger } from '@eluve/logger';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';

type DownloadStatus = 'idle' | 'loading' | 'success' | 'error';

const DownloadingDialog: React.FC<{
  status: DownloadStatus;
  isOpen: boolean;
  onClose: () => void;
}> = ({ status, isOpen, onClose }) => {
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('tenantAdmin.codes.downloadingTitle')}</DialogTitle>
        </DialogHeader>
        <Stack justify="center" align="center" className="py-6" gap={2}>
          {status === 'loading' && (
            <>
              <LoadingIcon
                size="2xl"
                className="mb-4 text-text-accent-secondary"
              />
              <Text>{t('tenantAdmin.codes.preparingDownload')}</Text>
            </>
          )}
          {status === 'success' && (
            <>
              <Icon
                name="CheckCircle2"
                size="2xl"
                className="mb-4 text-text-positive"
              />
              <Text>{t('tenantAdmin.codes.downloadComplete')}</Text>
            </>
          )}
          {status === 'error' && (
            <>
              <Icon
                name="AlertCircle"
                size="2xl"
                className="mb-4 text-text-negative"
              />
              <Text>{t('tenantAdmin.codes.downloadFailed')}</Text>
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export const DownloadCodesButton: React.FC<{
  hasEnabledCodes: boolean;
}> = ({ hasEnabledCodes }) => {
  const { t } = useTranslation();
  const tenantId = useAssignedTenantIdFromParams();
  const { userGroupId } = useParams() as { userGroupId: string };
  const logger = useNamedLogger('UserGroupCodeConfigs');

  const [downloadStatus, setDownloadStatus] = useState<DownloadStatus>('idle');
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);

  const apiClient = useApiClient();

  const downloadCodes = useCallback(
    async (downloadType: 'all' | 'group') => {
      setDownloadStatus('loading');
      setShowDownloadDialog(true);

      let response;
      let filename;
      let errorMessage;

      try {
        if (downloadType === 'all') {
          response =
            await apiClient.medicalCodes.generateDownloadUrlForAllCodes({
              params: { tenantId },
            });
          filename = 'all-available-medical-codes.csv';
          errorMessage = 'Error downloading medical codes:';
        } else {
          // Request a download URL for group-enabled codes
          response =
            await apiClient.medicalCodes.generateDownloadUrlForGroupEnabledCodes(
              {
                params: { tenantId, userGroupId },
                body: {},
              },
            );
          filename = 'group-enabled-medical-codes.csv';
          errorMessage = 'Error downloading group-enabled medical codes:';
        }

        if (response.status !== 200) {
          throw new Error('Failed to generate download URL');
        }

        const { downloadUrl } = response.body;

        // Create a temporary link to trigger the download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setDownloadStatus('success');

        // Automatically close the dialog after success (3 seconds)
        setTimeout(() => {
          if (downloadStatus === 'success') {
            setShowDownloadDialog(false);
            setDownloadStatus('idle');
          }
        }, 3000);
      } catch (error) {
        logger.error(errorMessage || 'Error downloading medical codes:', {
          error,
        });
        setDownloadStatus('error');
      }
    },
    [apiClient, tenantId, userGroupId, downloadStatus, logger],
  );

  const handleCloseDownloadDialog = useCallback(() => {
    setShowDownloadDialog(false);
    if (downloadStatus === 'error') {
      setDownloadStatus('idle');
    }
  }, [downloadStatus]);

  return (
    <>
      <Button onClick={() => downloadCodes('all')}>
        {t('tenantAdmin.codes.downloadAllCodes')}
      </Button>
      <Button
        onClick={() => downloadCodes('group')}
        disabled={!hasEnabledCodes}
      >
        {t('tenantAdmin.codes.downloadEnabledCodes')}
      </Button>

      {/* Download Status Dialog */}
      <DownloadingDialog
        status={downloadStatus}
        isOpen={showDownloadDialog}
        onClose={handleCloseDownloadDialog}
      />
    </>
  );
};
